const VIDEO_CAROUSEL_PLAY_EVENT = 'custom-video-carousel:play';

/**
 * A scroll-snap video rail with optional continuous looping. Visual buffers have
 * no video/source nodes; originals retain all playback, editor and logical state.
 */
class CustomVideoCarousel extends HTMLElement {
  static observedAttributes = ['data-initial-index', 'data-loop', 'data-muted', 'data-autoplay', 'data-video-loop'];
  static #rememberedCenters = new Map();

  #viewport;
  #track;
  #cards = [];
  #physicalCards = [];
  #mirrors = new Map();
  #cycleWidth = 0;
  #loopStyles;
  #controller;
  #resizeObserver;
  #intersectionObserver;
  #mutationObserver;
  #motion;
  #scrollTimer;
  #clickTimer;
  #resizeFrame;
  #refreshFrame;
  #scrollFrame;
  #index = 0;
  #centerIndex = 0;
  #centerId;
  #lastConfiguration;
  #targetIndex = null;
  #targetCard;
  #visible = false;
  #scrolling = false;
  #pointer;
  #touchCount = 0;
  #suppressClick = false;
  #geometry = '';
  #queuedPlay;
  #peerPlaying = false;
  #requestId = 0;
  #requests = new Map();
  #sourceFailures = new WeakMap();
  #knownVideos = new WeakSet();
  #loading = new WeakSet();
  #failed = new WeakSet();
  #userPaused = new WeakSet();
  #automatic = new WeakSet();
  #autoplayAttempted = new WeakSet();

  connectedCallback() {
    const remembered = CustomVideoCarousel.#rememberedCenters.get(this.#stateKey);
    if (!this.#centerId && remembered?.initialIndex === this.dataset.initialIndex) this.#centerId = remembered.blockId;
    this.#setup();
  }

  disconnectedCallback() {
    this.#rememberCenter();
    this.#cleanup();
  }

  attributeChangedCallback(name, previous, next) {
    if (previous === next || !this.isConnected || !this.#controller) return;
    if (name === 'data-initial-index') {
      this.#lastConfiguration.initialIndex = this.dataset.initialIndex;
      this.#queuedPlay = undefined;
      this.#go(this.#initialIndex, false);
    } else if (name === 'data-muted') {
      this.#lastConfiguration.muted = this.dataset.muted;
      for (const video of this.#videos) { video.muted = this.dataset.muted === 'true'; this.#syncVideo(video); }
    } else if (name === 'data-video-loop') {
      for (const video of this.#videos) video.loop = this.dataset.videoLoop === 'true';
    } else if (name === 'data-autoplay') {
      if (next !== 'true') for (const video of this.#videos) if (this.#automatic.has(video)) this.#pause(video);
      const video = this.#centerVideo;
      if (video) this.#autoplayAttempted.delete(video);
      this.#syncPlayback();
    } else if (name === 'data-loop') {
      this.#buildLoop();
      this.#restoreCenter();
    }
    this.#syncNavigation();
  }

  get #stateKey() { return this.id || this.dataset.sectionId; }
  get #loop() { return this.dataset.loop === 'true'; }
  get #rtl() { return getComputedStyle(this.#viewport).direction === 'rtl'; }
  get #initialIndex() {
    const requested = Number.parseInt(this.dataset.initialIndex, 10);
    return Number.isFinite(requested) ? requested - 1 : Math.floor(this.#cards.length / 2);
  }
  get #videos() { return this.#cards.flatMap(card => [...card.querySelectorAll('video[data-video]')]); }
  get #centerVideo() { return this.#cards[this.#centerIndex]?.querySelector('video[data-video]'); }

  #setup() {
    const initialChanged = this.#lastConfiguration && this.#lastConfiguration.initialIndex !== this.dataset.initialIndex;
    const mutedChanged = this.#lastConfiguration && this.#lastConfiguration.muted !== this.dataset.muted;
    if (initialChanged) this.#centerId = undefined;
    this.#lastConfiguration = { initialIndex: this.dataset.initialIndex, muted: this.dataset.muted };
    this.#cleanup();
    this.#viewport = this.querySelector('[data-carousel-viewport]');
    this.#track = this.querySelector('[data-carousel-track]');
    this.#cards = [...this.querySelectorAll('[data-card-index][data-block-id]')].filter(card => card.closest('custom-video-carousel') === this && !card.hasAttribute('data-carousel-clone'));
    this.#physicalCards = this.#cards;
    this.#observeStructure();
    if (!(this.#viewport instanceof HTMLElement) || !(this.#track instanceof HTMLElement)) return;
    this.#controller = new AbortController();
    const signal = this.#controller.signal;
    const listen = (node, event, handler, options = {}) => node?.addEventListener(event, handler, { ...options, signal });
    this.#motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#visible = false;
    this.#scrolling = false;
    this.#targetIndex = null;
    this.#targetCard = undefined;
    this.#geometry = '';
    this.#suppressClick = false;

    for (const video of this.#videos) {
      video.controls = false;
      video.preload = 'none';
      video.playsInline = true;
      video.loop = this.dataset.videoLoop === 'true';
      if (mutedChanged || !this.#knownVideos.has(video)) {
        video.muted = this.dataset.muted === 'true';
        this.#knownVideos.add(video);
      }
      listen(video, 'play', () => this.#onVideoPlay(video));
      listen(video, 'playing', () => {
        if (video.paused) return;
        this.#loading.delete(video); this.#failed.delete(video); this.#announce(''); this.#syncVideo(video);
      });
      listen(video, 'pause', () => { this.#loading.delete(video); this.#syncVideo(video); this.#refreshMirrors(video); });
      listen(video, 'ended', () => { this.#loading.delete(video); this.#automatic.delete(video); this.#syncVideo(video); });
      listen(video, 'volumechange', () => this.#syncVideo(video));
      listen(video, 'waiting', () => {
        if (video.paused || video !== this.#centerVideo) return;
        this.#loading.add(video); this.#announce(this.dataset.loadingMessage); this.#syncVideo(video);
      });
      listen(video, 'canplay', () => { this.#loading.delete(video); this.#syncVideo(video); this.#refreshMirrors(video); });
      listen(video, 'loadeddata', () => this.#refreshMirrors(video));
      listen(video, 'seeked', () => this.#refreshMirrors(video));
      listen(video, 'error', () => this.#playbackError(video, this.dataset.playbackErrorMessage));
      for (const source of video.querySelectorAll('source')) listen(source, 'error', () => {
        const failures = this.#sourceFailures.get(video) || new Set();
        failures.add(source);
        this.#sourceFailures.set(video, failures);
        // A failed HLS candidate must not interrupt a usable MP4 fallback.
        if (this.#allSourcesFailed(video)) this.#playbackError(video, this.dataset.playbackErrorMessage);
      });
      this.#syncVideo(video);
    }

    listen(this, 'click', event => this.#onClick(event));
    listen(this.#viewport, 'click', event => {
      if (!this.#suppressClick) return;
      this.#suppressClick = false;
      clearTimeout(this.#clickTimer);
      event.preventDefault(); event.stopPropagation();
    }, { capture: true });
    listen(this.#viewport, 'scroll', () => this.#onScroll(), { passive: true });
    listen(this.#viewport, 'scrollend', () => this.#settle());
    listen(this.#viewport, 'wheel', () => this.#interruptNavigation(), { passive: true });
    listen(this.#viewport, 'keydown', event => this.#onKeydown(event));
    listen(this.#viewport, 'pointerdown', event => this.#onPointerDown(event));
    listen(this.#viewport, 'pointerdown', event => {
      if (!(event.target instanceof Element) || !event.target.closest('[data-carousel-clone]')) return;
      if (event.target.closest('button,a,input,textarea,select,[contenteditable]')) {
        event.preventDefault();
        this.#viewport.focus({ preventScroll: true });
      }
    }, { capture: true });
    listen(this.#viewport, 'pointermove', event => this.#onPointerMove(event));
    listen(this.#viewport, 'pointerup', event => this.#finishPointer(event));
    listen(this.#viewport, 'pointercancel', event => this.#finishPointer(event));
    listen(this.#viewport, 'lostpointercapture', event => this.#finishPointer(event));
    // Native panning cancels its PointerEvent stream before the finger lifts.
    // Touch events keep the settle timer from snapping during a held gesture.
    listen(this.#viewport, 'touchstart', event => {
      this.#touchCount = event.touches.length;
      if (!(event.target instanceof Element) || !event.target.closest('button,a,input,textarea,select,[contenteditable],video[controls]')) this.#interruptNavigation();
    }, { passive: true });
    const finishTouch = event => {
      if (!this.#touchCount) return;
      this.#touchCount = event.touches.length;
      if (!this.#touchCount) this.#scheduleSettle();
    };
    listen(document, 'touchend', finishTouch, { passive: true });
    listen(document, 'touchcancel', finishTouch, { passive: true });
    listen(document, 'pointerup', event => this.#finishPointer(event));
    listen(document, 'pointercancel', event => this.#finishPointer(event));
    listen(this.#viewport, 'dragstart', event => {
      if (event.target instanceof Element && !event.target.closest('a,button,input,textarea,select')) event.preventDefault();
    });
    listen(document, 'visibilitychange', () => {
      if (document.hidden) { this.#pauseAll(); return; }
      const video = this.#centerVideo;
      if (video) this.#autoplayAttempted.delete(video);
      this.#restoreCenter();
    });
    listen(document, VIDEO_CAROUSEL_PLAY_EVENT, event => {
      if (event.detail?.carousel === this) return;
      this.#peerPlaying = true;
      this.#queuedPlay = undefined;
      this.#pauseAll();
    });
    listen(this.#motion, 'change', () => {
      if (this.#motion.matches) {
        for (const video of this.#videos) if (this.#automatic.has(video)) this.#pause(video);
      } else if (this.#centerVideo) this.#autoplayAttempted.delete(this.#centerVideo);
      this.#restoreCenter();
    });
    listen(document, 'shopify:block:select', event => {
      const index = this.#cards.findIndex(card => card.dataset.blockId === event.detail?.blockId);
      if (index !== -1) { this.#queuedPlay = undefined; this.#go(index, false); }
    });
    listen(document, 'shopify:section:reorder', event => {
      if (this.contains(event.target) || event.target?.contains?.(this) || event.detail?.sectionId === this.dataset.sectionId) this.#restoreCenter();
    });
    listen(document, 'shopify:section:select', event => {
      if (this.contains(event.target) || event.target?.contains?.(this) || event.detail?.sectionId === this.dataset.sectionId) this.#restoreCenter();
    });
    listen(window, 'resize', () => this.#queueResize(), { passive: true });

    this.#resizeObserver = new ResizeObserver(() => this.#queueResize());
    this.#resizeObserver.observe(this.#viewport);
    this.#resizeObserver.observe(this.#track);
    for (const card of this.#cards) this.#resizeObserver.observe(card);
    if ('IntersectionObserver' in window) {
      this.#intersectionObserver = new IntersectionObserver(entries => {
        const visible = entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 0.15);
        if (this.#visible === visible) return;
        this.#visible = visible;
        if (!visible) this.#pauseAll();
        else {
          if (this.#centerVideo) this.#autoplayAttempted.delete(this.#centerVideo);
          this.#syncPlayback();
        }
      }, { threshold: [0, 0.15] });
      this.#intersectionObserver.observe(this.#viewport);
    } else {
      const bounds = this.#viewport.getBoundingClientRect();
      this.#visible = bounds.bottom > 0 && bounds.top < window.innerHeight;
    }
    this.#buildLoop();
    this.dataset.enhanced = 'true';
    const restored = this.#cards.findIndex(card => card.dataset.blockId === this.#centerId);
    this.#go(restored === -1 ? this.#initialIndex : restored, false);
    this.#syncNavigation();
  }

  #buildLoop() {
    if (!this.#viewport || !this.#track) return;
    const active = this.#cards[Math.min(this.#centerIndex, this.#cards.length - 1)];
    const previousDistance = active ? this.#distanceToCard(active) : 0;
    const snap = this.#viewport.style.scrollSnapType;
    this.#viewport.style.scrollSnapType = 'none';
    this.#mutationObserver?.disconnect();
    for (const clone of this.#track.querySelectorAll('[data-carousel-clone]')) clone.remove();
    this.#mirrors.clear();
    this.#physicalCards = this.#cards;
    this.#cycleWidth = 0;
    this.#targetCard = undefined;
    if (this.#loop && this.#cards.length > 1) {
      if (!this.#loopStyles) this.#loopStyles = { viewportAnchor: this.#viewport.style.overflowAnchor, trackAnchor: this.#track.style.overflowAnchor };
      this.#viewport.style.overflowAnchor = 'none';
      this.#track.style.overflowAnchor = 'none';
      const gap = Number.parseFloat(getComputedStyle(this.#track).columnGap) || 0;
      const widths = this.#cards.map(card => card.getBoundingClientRect().width);
      const first = this.#cards[0].getBoundingClientRect(), last = this.#cards.at(-1).getBoundingClientRect();
      this.#cycleWidth = Math.abs(last.left - first.left) + last.width + gap;
      if (this.#cycleWidth > 0) {
        const coverage = Math.max(0, (this.#viewport.clientWidth - Math.min(...widths)) / 2) + Math.max(...widths) + gap;
        const bands = Math.max(1, Math.ceil(coverage / this.#cycleWidth));
        const before = document.createDocumentFragment(), after = document.createDocumentFragment();
        for (let band = 0; band < bands; band++) {
          this.#cards.forEach((card, index) => before.append(this.#cloneCard(card, index)));
          this.#cards.forEach((card, index) => after.append(this.#cloneCard(card, index)));
        }
        this.#track.prepend(before);
        this.#track.append(after);
        this.#physicalCards = [...this.#track.children].filter(card => card.hasAttribute('data-card-index'));
      }
    } else this.#restoreLoopStyles();
    if (active) this.#scrollBy(this.#distanceToCard(active) - previousDistance, false);
    // Read layout while snapping is disabled; restoring snapping then sees the
    // same visible center instead of an intermediate buffer insertion position.
    this.#viewport.getBoundingClientRect();
    this.#viewport.style.scrollSnapType = snap;
    for (const video of this.#videos) this.#refreshMirrors(video);
    this.#mutationObserver?.observe(this, { childList: true, subtree: true });
  }

  #cloneCard(card, index) {
    const clone = card.cloneNode(true);
    clone.dataset.carouselClone = 'true';
    clone.dataset.carouselLogicalIndex = String(index);
    clone.removeAttribute('data-block-id');
    clone.removeAttribute('data-active');
    clone.removeAttribute('aria-current');
    clone.setAttribute('aria-hidden', 'true');
    for (const node of [clone, ...clone.querySelectorAll('*')]) {
      for (const attribute of [...node.attributes]) {
        if (attribute.name === 'id' || attribute.name.startsWith('data-shopify-') || ['aria-controls', 'aria-labelledby', 'aria-describedby'].includes(attribute.name)) node.removeAttribute(attribute.name);
      }
      if (node.matches('a,button,input,textarea,select,[tabindex],[contenteditable]')) {
        node.setAttribute('tabindex', '-1');
        node.removeAttribute('autofocus');
        node.removeAttribute('contenteditable');
      }
    }
    const originals = [...card.querySelectorAll('video[data-video]')];
    [...clone.querySelectorAll('video')].forEach((copy, position) => {
      const video = originals[position];
      const mirror = document.createElement('div');
      mirror.className = copy.className;
      mirror.setAttribute('aria-hidden', 'true');
      const computed = getComputedStyle(video || copy);
      mirror.style.backgroundColor = computed.backgroundColor;
      mirror.style.backgroundSize = computed.objectFit === 'contain' ? 'contain' : 'cover';
      mirror.style.backgroundPosition = computed.objectPosition;
      mirror.style.backgroundRepeat = 'no-repeat';
      if (video?.poster) mirror.style.backgroundImage = `url(${JSON.stringify(video.poster)})`;
      copy.replaceWith(mirror);
      if (video) {
        const mirrors = this.#mirrors.get(video) || new Set();
        mirrors.add({ node: mirror, poster: mirror });
        this.#mirrors.set(video, mirrors);
        this.#refreshMirrors(video);
      }
    });
    return clone;
  }

  #refreshMirrors(video) {
    if (!this.#mirrors.has(video)) return;
    const viewport = this.#viewport.getBoundingClientRect();
    const size = video.closest('[data-card-index]').getBoundingClientRect();
    const drawable = video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && video.videoWidth && video.videoHeight;
    // Keep snapshots close to the viewport and bounded in resolution. A long
    // rail must not retain full-resolution decoded copies for every video.
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const fit = getComputedStyle(video).objectFit;
    const displayScale = (fit === 'contain' ? Math.min : Math.max)(size.width / video.videoWidth, size.height / video.videoHeight) * dpr;
    const scale = Math.min(displayScale, 1024 / Math.max(video.videoWidth, video.videoHeight), 1);
    const width = Math.max(1, Math.round(video.videoWidth * scale)), height = Math.max(1, Math.round(video.videoHeight * scale));
    for (const mirror of this.#mirrors.get(video) || []) {
      const bounds = mirror.node.getBoundingClientRect();
      if (!mirror.node.isConnected || bounds.right < viewport.left - size.width || bounds.left > viewport.right + size.width) {
        if (mirror.node instanceof HTMLCanvasElement) {
          const canvas = mirror.node;
          canvas.replaceWith(mirror.poster);
          mirror.node = mirror.poster;
          canvas.width = 0; canvas.height = 0;
        }
        continue;
      }
      if (!drawable) continue;
      const changed = !(mirror.node instanceof HTMLCanvasElement) || mirror.node.width !== width || mirror.node.height !== height;
      const canvas = changed ? document.createElement('canvas') : mirror.node;
      if (canvas.width !== width) canvas.width = width;
      if (canvas.height !== height) canvas.height = height;
      if (!changed && canvas === mirror.node && mirror.time === video.currentTime) continue;
      try {
        const context = canvas.getContext('2d');
        if (!context) continue;
        // Drawing cross-origin media is allowed; no pixels are read or exported.
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
      } catch { continue; }
      mirror.time = video.currentTime;
      if (canvas !== mirror.node) {
        const previous = mirror.node;
        canvas.className = mirror.node.className;
        canvas.setAttribute('aria-hidden', 'true');
        mirror.node.replaceWith(canvas);
        mirror.node = canvas;
        if (previous instanceof HTMLCanvasElement) { previous.width = 0; previous.height = 0; }
      }
    }
  }

  #restoreLoopStyles() {
    if (!this.#loopStyles) return;
    if (this.#viewport) this.#viewport.style.overflowAnchor = this.#loopStyles.viewportAnchor;
    if (this.#track) this.#track.style.overflowAnchor = this.#loopStyles.trackAnchor;
    this.#loopStyles = undefined;
  }

  #logicalIndex(card) {
    if (!card) return 0;
    return card.hasAttribute('data-carousel-clone') ? Number(card.dataset.carouselLogicalIndex) : this.#cards.indexOf(card);
  }

  #rebase(card = this.#nearestCard()) {
    if (!this.#loop || !card?.hasAttribute('data-carousel-clone')) return this.#cards[this.#logicalIndex(card)] || card;
    const original = this.#cards[this.#logicalIndex(card)];
    if (!original) return card;
    for (const video of this.#videos) this.#refreshMirrors(video);
    const delta = this.#distanceToCard(original) - this.#distanceToCard(card);
    const snap = this.#viewport.style.scrollSnapType;
    this.#viewport.style.scrollSnapType = 'none';
    this.#scrollBy(delta, false);
    this.#viewport.getBoundingClientRect();
    // A different band is now visible. Populate its paused frames before paint,
    // including when the deferred scroll event sees an already settled rail.
    for (const video of this.#videos) this.#refreshMirrors(video);
    this.#viewport.style.scrollSnapType = snap;
    return original;
  }

  #chooseTarget(index, direction, preferred) {
    if (!this.#loop || this.#physicalCards.length === this.#cards.length) return this.#cards[index];
    if (preferred && this.#physicalCards.includes(preferred) && this.#logicalIndex(preferred) === index) return preferred;
    const anchor = this.#targetCard || this.#nearestCard();
    if (Math.abs(direction) === 1) {
      const neighbor = this.#physicalCards[this.#physicalCards.indexOf(anchor) + direction];
      if (neighbor && this.#logicalIndex(neighbor) === index && Math.abs(this.#distanceToCard(neighbor)) <= this.#cycleWidth) return neighbor;
      // Coalesce an unusually long burst before it reaches the outer buffer.
      // Rebasing preserves pixels, then the newest target gets one short path.
      this.#rebase();
    }
    let candidates = this.#physicalCards.filter(card => this.#logicalIndex(card) === index);
    if (Math.abs(direction) === 1) {
      const forward = candidates.filter(card => this.#distanceToCard(card) * direction * (this.#rtl ? -1 : 1) >= -1);
      if (forward.length) candidates = forward;
    }
    return candidates.reduce((nearest, card) => !nearest || Math.abs(this.#distanceToCard(card)) < Math.abs(this.#distanceToCard(nearest)) ? card : nearest, undefined);
  }

  #observeStructure() {
    this.#mutationObserver = new MutationObserver(records => {
      const structural = records.some(record => [...record.addedNodes, ...record.removedNodes].some(node => node instanceof Element && !node.closest('[data-carousel-clone]') && !node.hasAttribute('data-carousel-clone') && (node.matches('[data-carousel-viewport],[data-carousel-track],[data-card-index],video[data-video],source') || node.querySelector('[data-carousel-viewport],[data-carousel-track],[data-card-index],video[data-video],source'))));
      if (!structural) return;
      cancelAnimationFrame(this.#refreshFrame);
      this.#refreshFrame = requestAnimationFrame(() => { if (this.isConnected) this.#setup(); });
    });
    this.#mutationObserver.observe(this, { childList: true, subtree: true });
  }

  #onClick(event) {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-carousel-previous],[data-carousel-next],[data-play-toggle],[data-mute-toggle]');
    if (!button || button.closest('custom-video-carousel') !== this || button.disabled) return;
    if (button.matches('[data-carousel-previous],[data-carousel-next]')) {
      this.#queuedPlay = undefined;
      this.#peerPlaying = false;
      this.#go(this.#index + (button.hasAttribute('data-carousel-next') ? 1 : -1));
      return;
    }
    const physicalCard = button.closest('[data-card-index]');
    const card = this.#cards[this.#logicalIndex(physicalCard)];
    const video = card?.querySelector('video[data-video]');
    if (!(video instanceof HTMLVideoElement)) return;
    if (button.hasAttribute('data-mute-toggle')) {
      video.muted = !video.muted;
      this.#syncVideo(video);
      return;
    }
    if (!video.paused || this.#loading.has(video)) {
      this.#userPaused.add(video);
      this.#queuedPlay = undefined;
      this.#pause(video);
      this.#announce('');
      return;
    }
    this.#peerPlaying = false;
    this.#userPaused.delete(video);
    const index = this.#cards.indexOf(card);
    if (index !== this.#centerIndex || this.#scrolling) {
      this.#queuedPlay = video;
      this.#go(index, true, physicalCard);
    } else this.#requestPlay(video, false);
  }

  #onKeydown(event) {
    // Native media controls and all card buttons retain their own key handling.
    if (event.target !== this.#viewport || event.altKey || event.ctrlKey || event.metaKey) return;
    let next;
    if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = this.#cards.length - 1;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') next = this.#index + (event.key === 'ArrowRight' ? 1 : -1) * (this.#rtl ? -1 : 1);
    else return;
    event.preventDefault();
    this.#queuedPlay = undefined;
    this.#peerPlaying = false;
    this.#go(next);
  }

  #bounded(index) {
    const count = this.#cards.length;
    if (!count) return 0;
    return this.#loop ? ((index % count) + count) % count : Math.max(0, Math.min(index, count - 1));
  }

  #distance(index) {
    return this.#distanceToCard(this.#cards[index]);
  }

  #distanceToCard(element) {
    const viewport = this.#viewport.getBoundingClientRect();
    const card = element?.getBoundingClientRect();
    return card ? card.left + card.width / 2 - viewport.left - viewport.width / 2 : 0;
  }

  #nearestCard() {
    return this.#physicalCards.reduce((nearest, card) => !nearest || Math.abs(this.#distanceToCard(card)) < Math.abs(this.#distanceToCard(nearest)) ? card : nearest, undefined);
  }

  #nearest() { return this.#logicalIndex(this.#nearestCard()); }

  #scrollBy(delta, smooth) {
    if (smooth && !this.#motion?.matches) this.#viewport.scrollBy({ left: delta, behavior: 'smooth' });
    else {
      const previous = this.#viewport.style.scrollBehavior;
      this.#viewport.style.scrollBehavior = 'auto';
      this.#viewport.scrollBy({ left: delta, behavior: 'instant' });
      this.#viewport.style.scrollBehavior = previous;
    }
  }

  #go(index, animate = true, preferred) {
    if (!this.#viewport) return;
    if (!this.#cards.length) {
      this.#targetIndex = null;
      this.#targetCard = undefined;
      this.#index = 0;
      this.#centerIndex = 0;
      this.#centerId = undefined;
      this.#scrolling = false;
      this.dataset.activeIndex = '0';
      this.dataset.activeBlockId = '';
      this.#syncNavigation();
      return;
    }
    const direction = index - this.#index;
    this.#index = this.#bounded(index);
    const target = animate ? this.#chooseTarget(this.#index, direction, preferred) : this.#cards[this.#index];
    this.#centerId = this.#cards[this.#index].dataset.blockId;
    this.#rememberCenter();
    const delta = this.#distanceToCard(target);
    this.#targetIndex = this.#index;
    this.#targetCard = target;
    this.#scrolling = animate && !this.#motion?.matches && Math.abs(delta) > 1;
    if (this.#scrolling || this.#index !== this.#centerIndex) this.#pauseAll();
    this.#scrollBy(delta, this.#scrolling);
    this.#syncNavigation();
    if (!this.#scrolling) this.#settle();
    else this.#scheduleSettle();
  }

  #onScroll() {
    if (!this.#cards.length || !this.#viewport) return;
    // Instant recentering can dispatch a deferred scroll event after playback has
    // resumed. An already centered, idle rail needs no second pause/settle cycle.
    const nearestCard = this.#nearestCard();
    if (!this.#scrolling && this.#logicalIndex(nearestCard) === this.#centerIndex && Math.abs(this.#distanceToCard(nearestCard)) <= 1) return;
    this.#scrolling = true;
    this.#pauseAll();
    // Native touch, wheel and held mouse gestures can travel beyond either end
    // of the original band. Translate by one equivalent band before exhausting
    // the buffers; the gesture retains its partial-card offset.
    if (this.#targetIndex === null) this.#rebase(nearestCard);
    for (const video of this.#videos) this.#refreshMirrors(video);
    cancelAnimationFrame(this.#scrollFrame);
    this.#scrollFrame = requestAnimationFrame(() => {
      if (!this.isConnected || !this.#cards.length) return;
      const nearest = this.#nearest();
      this.#applyCenter(nearest);
      if (this.#targetIndex === null) {
        this.#index = nearest;
        this.#centerId = this.#cards[nearest].dataset.blockId;
        this.#syncNavigation();
      }
    });
    this.#scheduleSettle();
  }

  #scheduleSettle() {
    clearTimeout(this.#scrollTimer);
    this.#scrollTimer = setTimeout(() => this.#settle(), 160);
  }

  #settle() {
    if (!this.isConnected || !this.#viewport || !this.#cards.length || this.#pointer?.dragging || this.#touchCount) return;
    clearTimeout(this.#scrollTimer);
    cancelAnimationFrame(this.#scrollFrame);
    const target = this.#targetCard || this.#nearestCard();
    const index = this.#targetIndex ?? this.#logicalIndex(target);
    // A last geometry-based correction makes rapid navigation settle on its final
    // requested card, including RTL's negative scrollLeft coordinate system.
    if (Math.abs(this.#distanceToCard(target)) > 1) this.#scrollBy(this.#distanceToCard(target), false);
    this.#rebase(target);
    // Instant editor navigation and resize can expose a different visual band
    // without rebasing or another scroll event. Refresh its frames before paint.
    for (const video of this.#videos) this.#refreshMirrors(video);
    this.#targetIndex = null;
    this.#targetCard = undefined;
    this.#scrolling = false;
    this.#index = index;
    this.#centerId = this.#cards[index].dataset.blockId;
    this.#applyCenter(index);
    this.#syncNavigation();
    this.#rememberCenter();
    const queued = this.#queuedPlay;
    this.#queuedPlay = undefined;
    if (queued && queued === this.#centerVideo) this.#requestPlay(queued, false);
    else this.#syncPlayback();
  }

  #applyCenter(index) {
    const previous = this.#centerVideo;
    this.#centerIndex = index;
    const current = this.#centerVideo;
    if (previous !== current && current) this.#autoplayAttempted.delete(current);
    this.dataset.activeIndex = String(index + 1);
    this.dataset.activeBlockId = this.#cards[index]?.dataset.blockId || '';
    this.#cards.forEach((card, position) => {
      const active = position === index;
      card.classList.toggle('is-active', active);
      card.dataset.active = String(active);
      card.setAttribute('aria-current', String(active));
      for (const video of card.querySelectorAll('video[data-video]')) if (!active) this.#pause(video);
    });
    for (const card of this.#physicalCards) if (card.hasAttribute('data-carousel-clone')) card.classList.toggle('is-active', this.#logicalIndex(card) === index);
  }

  #syncNavigation() {
    for (const button of this.querySelectorAll('[data-carousel-previous]')) button.disabled = this.#cards.length < 2 || (!this.#loop && this.#index === 0);
    for (const button of this.querySelectorAll('[data-carousel-next]')) button.disabled = this.#cards.length < 2 || (!this.#loop && this.#index === this.#cards.length - 1);
  }

  #interruptNavigation() {
    if (!this.#cards.length || !this.#viewport) return;
    this.#targetIndex = null;
    this.#targetCard = undefined;
    this.#queuedPlay = undefined;
    this.#index = this.#nearest();
    this.#centerId = this.#cards[this.#index]?.dataset.blockId;
    this.#scrolling = true;
    this.#pauseAll();
    this.#scheduleSettle();
  }

  #onPointerDown(event) {
    if (!event.isPrimary || event.button > 0 || this.#cards.length < 2) return;
    if (event.target instanceof Element && event.target.closest('button,a,input,textarea,select,[contenteditable],video[controls]')) return;
    this.#suppressClick = false;
    clearTimeout(this.#clickTimer);
    this.#interruptNavigation();
    if (event.pointerType !== 'mouse') return;
    this.#pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, lastX: event.clientX, dragging: false, snap: this.#viewport.style.scrollSnapType, behavior: this.#viewport.style.scrollBehavior };
  }

  #onPointerMove(event) {
    const pointer = this.#pointer;
    if (!pointer || pointer.id !== event.pointerId) return;
    if (!pointer.dragging) {
      const horizontal = Math.abs(event.clientX - pointer.x), vertical = Math.abs(event.clientY - pointer.y);
      if (vertical > 8 && vertical > horizontal) { this.#pointer = undefined; return; }
      if (horizontal < 6) return;
      pointer.dragging = true;
      this.#viewport.setPointerCapture(event.pointerId);
      this.#viewport.style.scrollSnapType = 'none';
      this.#viewport.style.scrollBehavior = 'auto';
      this.classList.add('is-dragging');
    }
    event.preventDefault();
    this.#viewport.scrollBy({ left: pointer.lastX - event.clientX, behavior: 'instant' });
    this.#rebase();
    pointer.lastX = event.clientX;
  }

  #finishPointer(event) {
    const pointer = this.#pointer;
    if (!pointer || pointer.id !== event.pointerId) return;
    this.#pointer = undefined;
    this.#viewport.style.scrollSnapType = pointer.snap;
    this.#viewport.style.scrollBehavior = pointer.behavior;
    if (this.#viewport.hasPointerCapture(event.pointerId)) this.#viewport.releasePointerCapture(event.pointerId);
    this.classList.remove('is-dragging');
    if (pointer.dragging) {
      this.#suppressClick = true;
      this.#clickTimer = setTimeout(() => { this.#suppressClick = false; }, 400);
    }
    this.#targetIndex = null;
    this.#targetCard = undefined;
    const nearest = this.#nearestCard();
    this.#go(this.#logicalIndex(nearest), true, nearest);
  }

  #queueResize() {
    cancelAnimationFrame(this.#resizeFrame);
    this.#resizeFrame = requestAnimationFrame(() => {
      if (!this.isConnected || !this.#viewport || this.#pointer?.dragging || this.#touchCount) return;
      const signature = [this.#viewport.clientWidth, ...this.#cards.map(card => card.getBoundingClientRect().width), getComputedStyle(this.#track).columnGap, this.#rtl].join(':');
      if (signature === this.#geometry) return;
      this.#geometry = signature;
      this.#buildLoop();
      this.#restoreCenter();
    });
  }

  #restoreCenter() {
    const restored = this.#cards.findIndex(card => card.dataset.blockId === this.#centerId);
    this.#go(restored === -1 ? this.#index : restored, false);
  }

  #rememberCenter() {
    if (!this.#stateKey || !this.#centerId) return;
    CustomVideoCarousel.#rememberedCenters.set(this.#stateKey, { blockId: this.#centerId, initialIndex: this.dataset.initialIndex });
    if (CustomVideoCarousel.#rememberedCenters.size > 100) CustomVideoCarousel.#rememberedCenters.delete(CustomVideoCarousel.#rememberedCenters.keys().next().value);
  }

  #syncPlayback() {
    const video = this.#centerVideo;
    for (const other of this.#videos) if (other !== video) this.#pause(other);
    if (!video || this.#scrolling || !this.#visible || document.hidden || this.#peerPlaying || this.dataset.autoplay !== 'true' || this.#motion?.matches || this.#userPaused.has(video) || this.#failed.has(video) || this.#autoplayAttempted.has(video) || !video.paused) return;
    this.#autoplayAttempted.add(video);
    this.#requestPlay(video, true);
  }

  #activateSources(video) {
    let changed = false;
    for (const node of [video, ...video.querySelectorAll('source')]) {
      const source = node.dataset.src || node.dataset.videoSource;
      if (source && node.getAttribute('src') !== source) { node.setAttribute('src', source); changed = true; }
    }
    if (changed) { this.#sourceFailures.delete(video); video.load(); }
    return changed;
  }

  #allSourcesFailed(video) {
    // A video-level src takes precedence over child source candidates.
    if (video.getAttribute('src')) return false;
    const candidates = [...video.querySelectorAll('source')].filter(source => source.getAttribute('src'));
    const failures = this.#sourceFailures.get(video);
    return candidates.length > 0 && candidates.every(source => failures?.has(source) || (source.type && video.canPlayType(source.type) === ''));
  }

  #requestPlay(video, automatic) {
    if (video !== this.#centerVideo || this.#scrolling || !this.#visible || document.hidden) return;
    this.#pauseAll(video);
    const retry = this.#failed.has(video) || video.error || video.networkState === HTMLMediaElement.NETWORK_NO_SOURCE;
    const attached = this.#activateSources(video);
    if (retry && !attached) { this.#sourceFailures.delete(video); video.load(); }
    if (automatic) { video.muted = true; this.#automatic.add(video); }
    else this.#automatic.delete(video);
    const request = ++this.#requestId;
    this.#requests.set(video, request);
    this.#loading.add(video);
    this.#failed.delete(video);
    this.#announce(this.dataset.loadingMessage);
    this.#syncVideo(video);
    if (this.#allSourcesFailed(video)) { this.#playbackError(video, this.dataset.playbackErrorMessage); return; }
    let playback;
    try { playback = video.play(); }
    catch (error) { this.#rejectPlayback(video, request, error); return; }
    Promise.resolve(playback).then(() => {
      // A previous request may finish after a later click has already succeeded.
      // Its completion must not pause or otherwise overwrite that newer state.
      if (this.#requests.get(video) !== request) return;
      this.#requests.delete(video);
      if (!this.isConnected || video !== this.#centerVideo || this.#scrolling || !this.#visible || document.hidden) { this.#pause(video); return; }
      this.#loading.delete(video);
      this.#announce('');
      this.#syncVideo(video);
    }).catch(error => this.#rejectPlayback(video, request, error));
  }

  #rejectPlayback(video, request, error) {
    if (this.#requests.get(video) !== request) return;
    this.#requests.delete(video);
    this.#playbackError(video, error?.name === 'NotAllowedError' ? this.dataset.playbackBlockedMessage : this.dataset.playbackErrorMessage);
  }

  #playbackError(video, message) {
    this.#pause(video);
    this.#failed.add(video);
    this.#syncVideo(video);
    if (video === this.#centerVideo) this.#announce(message);
  }

  #onVideoPlay(video) {
    if (video.paused || video.ended) { this.#syncVideo(video); return; }
    if (video !== this.#centerVideo || this.#scrolling || !this.#visible || document.hidden) { this.#pause(video); return; }
    this.#peerPlaying = false;
    this.#failed.delete(video);
    this.#syncVideo(video);
    document.dispatchEvent(new CustomEvent(VIDEO_CAROUSEL_PLAY_EVENT, { detail: { carousel: this } }));
  }

  #pause(video) {
    const pending = this.#requests.has(video) || this.#loading.has(video);
    const playing = !video.paused;
    this.#requests.delete(video);
    this.#loading.delete(video);
    if (playing) video.pause();
    if (pending || playing) this.#syncVideo(video);
  }

  #pauseAll(except) {
    for (const video of this.#videos) if (video !== except) this.#pause(video);
    this.#announce('');
  }

  #syncVideo(video) {
    const original = video.closest('[data-card-index]');
    if (!original) return;
    const playing = !video.paused && !video.ended;
    const pending = this.#loading.has(video);
    const index = this.#cards.indexOf(original);
    const cards = [original, ...this.#physicalCards.filter(card => card.hasAttribute('data-carousel-clone') && this.#logicalIndex(card) === index)];
    for (const card of cards) {
      card.classList.toggle('is-playing', playing);
      card.classList.toggle('is-loading', pending);
      card.classList.toggle('is-error', this.#failed.has(video));
      const play = card.querySelector('[data-play-toggle]');
      if (play) {
        play.setAttribute('aria-pressed', String(playing || pending));
        const label = playing || pending ? play.dataset.pauseLabel : play.dataset.playLabel;
        if (label) play.setAttribute('aria-label', label);
      }
      const mute = card.querySelector('[data-mute-toggle]');
      if (mute) {
        mute.setAttribute('aria-pressed', String(video.muted));
        const label = video.muted ? mute.dataset.unmuteLabel : mute.dataset.muteLabel;
        if (label) mute.setAttribute('aria-label', label);
      }
    }
  }

  #announce(message) {
    const status = this.querySelector('[data-status]');
    if (status && status.textContent !== (message || '')) status.textContent = message || '';
  }

  #cleanup() {
    this.#controller?.abort();
    this.#controller = undefined;
    this.#resizeObserver?.disconnect();
    this.#intersectionObserver?.disconnect();
    this.#mutationObserver?.disconnect();
    clearTimeout(this.#scrollTimer);
    clearTimeout(this.#clickTimer);
    cancelAnimationFrame(this.#resizeFrame);
    cancelAnimationFrame(this.#refreshFrame);
    cancelAnimationFrame(this.#scrollFrame);
    if (this.#pointer && this.#viewport) {
      this.#viewport.style.scrollSnapType = this.#pointer.snap;
      this.#viewport.style.scrollBehavior = this.#pointer.behavior;
      if (this.#viewport.hasPointerCapture(this.#pointer.id)) this.#viewport.releasePointerCapture(this.#pointer.id);
    }
    this.#pointer = undefined;
    this.#touchCount = 0;
    this.#queuedPlay = undefined;
    this.#requests.clear();
    this.#pauseAll();
    for (const video of this.#videos) video.controls = true;
    for (const clone of this.#track?.querySelectorAll('[data-carousel-clone]') || []) clone.remove();
    this.#mirrors.clear();
    this.#physicalCards = this.#cards;
    this.#targetCard = undefined;
    this.#cycleWidth = 0;
    this.#restoreLoopStyles();
    this.classList.remove('is-dragging');
    delete this.dataset.enhanced;
  }
}

if (!customElements.get('custom-video-carousel')) customElements.define('custom-video-carousel', CustomVideoCarousel);
